import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { pharmaciesApi } from '../../services/pharmaciesService'
import type { 
  Pharmacy,
  PharmaciesQueryParams 
} from '../../types/api'

// Async thunks
export const fetchPharmacies = createAsyncThunk(
  'pharmacies/fetchPharmacies',
  async (params?: PharmaciesQueryParams) => {
    const response = await pharmaciesApi.getPharmacies(params)
    return response
  }
)

// State interface
interface PharmaciesState {
  pharmacies: Pharmacy[]
  loading: boolean
  error: string | null
  filters: {
    type503bOnly: boolean | null
  }
}

// Initial state
const initialState: PharmaciesState = {
  pharmacies: [],
  loading: false,
  error: null,
  filters: {
    type503bOnly: null,
  },
}

// Slice
const pharmaciesSlice = createSlice({
  name: 'pharmacies',
  initialState,
  reducers: {
    setType503bFilter: (state, action) => {
      state.filters.type503bOnly = action.payload
    },
    clearFilters: (state) => {
      state.filters = { type503bOnly: null }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch pharmacies
    builder
      .addCase(fetchPharmacies.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPharmacies.fulfilled, (state, action) => {
        state.loading = false
        state.pharmacies = action.payload
      })
      .addCase(fetchPharmacies.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch pharmacies'
      })
  },
})

// Export actions
export const { setType503bFilter, clearFilters, clearError } = pharmaciesSlice.actions

// Selectors
export const selectPharmacies = (state: { pharmacies: PharmaciesState }) => state.pharmacies.pharmacies
export const selectPharmaciesLoading = (state: { pharmacies: PharmaciesState }) => state.pharmacies.loading
export const selectPharmaciesError = (state: { pharmacies: PharmaciesState }) => state.pharmacies.error
export const selectPharmaciesFilters = (state: { pharmacies: PharmaciesState }) => state.pharmacies.filters

export default pharmaciesSlice.reducer